import React from 'react';

/**
 * Lo que la red sabe de un cliente, sin que haya que escribirlo a mano.
 *
 * Todo esto lo saca el sistema del router: la IP del servicio, el equipo instalado con su
 * marca, modelo y firmware, y de qué caja o AP cuelga. Antes se guardaba y no se mostraba
 * en ninguna pantalla, así que para el usuario era como si no existiera.
 *
 * El mismo componente se usa en la lista (compacto) y en la ficha (completo), para que las
 * dos digan exactamente lo mismo y no haya que arreglar dos sitios cuando algo cambie.
 */

// De qué tecnología es, según el equipo que el router encontró. Se prefiere eso a lo que
// alguien haya escrito en la ficha: el router refleja la instalación de hoy.
export const tecnologiaDe = (c) => {
  if (c.equipo_tipo === 'onu') return 'fibra';
  if (c.equipo_tipo === 'antena' || c.equipo_tipo === 'cpe') return 'radio';
  return c.tecnologia_instalada || null;
};

/**
 * En la base hay TRES formas de escribir dos tecnologías: `fibra` (64 clientes),
 * `radio_enlace` (79) y `radio` (57). Esta tabla solo conocía dos, así que a los 79 de
 * `radio_enlace` la ficha les decía «Tecnología: sin dato» — con el dato guardado.
 *
 * Se aceptan las tres en vez de «normalizar» la base: renombrar 79 filas para que encajen
 * en una tabla de dos entradas es arriesgar datos de clientes por una comodidad de la
 * pantalla. Aquí la traducción no le cuesta nada a nadie.
 */
const TECNO = {
  fibra: { icono: '🔵', label: 'Fibra', color: '#22D3EE' },
  radio: { icono: '📡', label: 'Radio', color: '#34D399' },
  radio_enlace: { icono: '📡', label: 'Radio', color: '#34D399' },
};

// «hace 3 horas» se entiende de un vistazo; una fecha con hora hay que interpretarla.
export const hace = (fecha) => {
  if (!fecha) return null;
  const min = Math.floor((Date.now() - new Date(fecha).getTime()) / 60000);
  if (min < 0) return null;
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
};

// Pasado un día sin verlo, el dato ya no describe la realidad: puede estar apagado o
// desconectado. Se avisa en vez de mostrarlo como si fuera de ahora.
const VIEJO_HORAS = 26;
const esViejo = (fecha) =>
  fecha ? (Date.now() - new Date(fecha).getTime()) / 3600000 > VIEJO_HORAS : false;

export default function RedDelCliente({ cliente: c, compacto = false }) {
  if (!c) return null;
  const tec = TECNO[tecnologiaDe(c)];
  const tieneAlgo = c.ip_asignada || c.equipo_nombre || c.sitio_nombre;
  const esFibra = tecnologiaDe(c) === 'fibra';
  // Los cinco datos del equipo salen todos del mismo sitio: o están los cinco o no hay ninguno.
  const hayEquipo = c.equipo_nombre || c.equipo_mac || c.equipo_marca || c.equipo_modelo;

  if (compacto) {
    /**
     * Cuando no hay nada que mostrar, decir POR QUÉ.
     *
     * Antes salía un guion mudo y había que ir a averiguarlo: son tres situaciones muy
     * distintas y solo una es un problema.
     *
     *   · de un proveedor  → su servicio no pasa por nuestro router. Está bien.
     *   · con cuenta pero sin IP → la cola existe en el MikroTik sin dirección de destino.
     *     Hay que ponérsela allá; el sistema no puede inventarla.
     *   · sin cuenta       → no se le puede seguir el rastro de ninguna forma.
     */
    if (!tieneAlgo) {
      if (c.proveedor_red_nombre) {
        return <span style={s.nada} title={`El servicio lo presta ${c.proveedor_red_nombre}`}>
          por su proveedor
        </span>;
      }
      if (c.mikrotik_usuario && c.mikrotik_tipo && c.mikrotik_tipo !== 'ninguno') {
        return <span style={s.ojo}
          title={`Su ${c.mikrotik_tipo === 'pppoe' ? 'cuenta' : 'cola'} «${c.mikrotik_usuario}» existe en el MikroTik pero sin dirección. Ponle la IP allá.`}>
          sin IP en su {c.mikrotik_tipo === 'pppoe' ? 'cuenta' : 'cola'}
        </span>;
      }
      return <span style={s.ojo} title="No tiene usuario ni cola en el MikroTik: no se le puede seguir el rastro">
        sin conexión anotada
      </span>;
    }
    return (
      <div>
        {c.ip_asignada && <div style={s.ip}>{c.ip_asignada}</div>}
        {tec && (
          <div style={{ ...s.sub, color: tec.color }}>
            {tec.icono} {tec.label}
            {c.equipo_modelo ? ` · ${c.equipo_modelo}` : ''}
          </div>
        )}
        {c.sitio_nombre && <div style={s.sub}>📦 {c.sitio_nombre}
          {c.puerto_etiqueta ? ` p.${c.puerto_etiqueta}` : ''}</div>}
      </div>
    );
  }

  return (
    <div style={s.caja}>
      <div style={s.titulo}>
        🛰️ Lo que la red sabe de este cliente
        {c.equipo_visto_en && (
          <span style={{ ...s.visto, color: esViejo(c.equipo_visto_en) ? '#FDE047' : 'rgba(255,255,255,0.35)' }}>
            {esViejo(c.equipo_visto_en) ? '⚠ ' : ''}visto por el router {hace(c.equipo_visto_en)}
          </span>
        )}
      </div>

      {!tieneAlgo ? (
        <div style={s.nada}>
          Todavía no. Corre <b>Red → Equipos → 🔎 Descubrir equipos de clientes</b> y el
          sistema lo saca del router solo.
        </div>
      ) : (
        <div style={s.filas}>
          <Dato l="IP del servicio" v={c.ip_asignada}
            nota={c.ip_origen === 'cola' ? 'de su cola' : c.ip_origen === 'pppoe' ? 'de su sesión PPPoE' : null} />
          <Dato l="Tecnología" v={tec ? `${tec.icono} ${tec.label}` : null} color={tec?.color} />
          {/* Sin equipo descubierto, estos cinco renglones dicen «sin dato» los cinco: no son
              cinco datos que falten, es uno solo —el equipo— que todavía no se ha buscado. */}
          {hayEquipo ? (
            <>
              <Dato l="Equipo instalado" v={c.equipo_nombre} />
              <Dato l="Marca y modelo"
                v={[c.equipo_marca, c.equipo_modelo].filter(Boolean).join(' ') || null}
                nota={!c.equipo_marca && c.equipo_mac ? 'marca sin identificar' : null} />
              <Dato l="Firmware" v={c.equipo_firmware} />
              <Dato l="MAC" v={c.equipo_mac} />
              {/* La ONU tiene su propia dirección de gestión, distinta de la del servicio.
                  Mostrarlas juntas evita que se confundan. */}
              <Dato l="IP de gestión del equipo"
                v={c.equipo_ip && c.equipo_ip !== c.ip_asignada ? c.equipo_ip : null} />
            </>
          ) : (
            <div style={s.nada}>
              Su equipo todavía no aparece. Lo saca del router{' '}
              <b>Red → Equipos → 🔎 Descubrir equipos de clientes</b>.
            </div>
          )}
          <Dato l="Cuelga de"
            v={c.sitio_nombre ? `${c.sitio_nombre}${c.puerto_etiqueta ? ` · puerto ${c.puerto_etiqueta}` : ''}` : null} />
          {/* El AP es cosa de radio: por fibra no se pasa por ninguno. */}
          {(c.ap_nombre || !esFibra) && <Dato l="Por el AP" v={c.ap_nombre} />}
        </div>
      )}
    </div>
  );
}

// Un dato que no se sabe se muestra igual, en gris: así se ve qué falta por descubrir en
// vez de dejar un hueco que parece un error de la pantalla.
const Dato = ({ l, v, nota, color }) => (
  <div style={s.fila}>
    <span style={s.etiqueta}>{l}</span>
    <span style={{ ...s.valor, color: v ? (color || '#fff') : 'rgba(255,255,255,0.25)' }}>
      {v || 'sin dato'}
      {v && nota && <span style={s.nota}> ({nota})</span>}
    </span>
  </div>
);

const s = {
  caja:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '10px', padding: '13px 15px', marginBottom: '14px' },
  titulo:   { fontSize: '12.5px', fontWeight: '700', color: '#A5B4FC', marginBottom: '10px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              gap: '10px', flexWrap: 'wrap' },
  visto:    { fontSize: '11px', fontWeight: '400' },
  filas:    { display: 'flex', flexDirection: 'column', gap: '5px' },
  fila:     { display: 'flex', gap: '10px', fontSize: '12.5px', alignItems: 'baseline' },
  etiqueta: { color: 'rgba(255,255,255,0.42)', width: '165px', flexShrink: 0 },
  valor:    { fontFamily: 'monospace', wordBreak: 'break-all' },
  nota:     { fontFamily: 'inherit', color: 'rgba(255,255,255,0.35)', fontSize: '11.5px' },
  nada:     { fontSize: '12px', color: 'rgba(255,255,255,0.35)', lineHeight: '1.6' },
  // Lo que sí hay que ir a arreglar: se distingue de lo que simplemente no aplica
  ojo:      { fontSize: '12px', color: '#FDBA74', lineHeight: '1.6', cursor: 'help' },
  ip:       { fontSize: '12px', fontFamily: 'monospace', color: '#7DD3FC' },
  sub:      { fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' },
};
